/**
 * `/count` — the shelf count. One screen, one bin per row, in shelf-walk order, on a phone at the
 * shelf or at a desktop keyboard.
 *
 * ⚠️ MONEY PATH. Every save writes a `COUNT` movement, which ANCHORS on-hand absolutely, and on-hand
 * drives reorder, coverage, "select in stock" and the purchase draft. Since pulled-blanks v2 (B2) the
 * count is a SHELF count: the counter types what is physically on the shelf, and the server adds what
 * the pull station recorded as pulled and not yet drawn (`book = shelf + Σ out`, derived SERVER-side at
 * write time from the ledger and the sheet headers — `rest-api/src/routes/inventory.ts` §2.8). Five
 * rules keep that safe, and all five are load-bearing:
 *
 * 1. **Only a typed number is ever posted.** The book figure and the pulled stacks this sheet loaded
 *    with are used for the variance display, the plausibility guard and the ADVISORY ids the post rides
 *    with (`pulledSeen`: sheet, record revision, the quantity shown) — never as a quantity the server
 *    adds. There is deliberately no "same as before" control: it would post a figure cached at page
 *    load, so a press settling during the walk would see that tap RESURRECT the garments it had just
 *    consumed. A typed number is a measurement of the shelf and is correct whatever the book has done.
 * 2. **The idempotency token identifies an OBSERVATION, not a bin** — it is keyed
 *    `<ssSku>:<attempt generation>:<figure>`, and NOTHING else enters it: not `asOf`, not the seen
 *    sheets, not a `pulled_rose` answer. The server dedupes on `SK = MOVE#<clientToken>`
 *    PAYLOAD-INDEPENDENTLY, so replaying a token with a CHANGED quantity makes the server keep the old
 *    figure and answer 200 — the screen would report a number the ledger does not hold. So: retrying
 *    the same figure replays its token (a dropped response must not double-write), a resubmit with an
 *    answer replays it too (a refusal wrote nothing), changing the figure mints a fresh one, and an
 *    explicit Re-count bumps the generation.
 * 3. **The row shows what the LEDGER recorded, never what we hoped it recorded.** The route returns
 *    the re-read `BlankOnHand` plus the COUNT row's split and the SERVER's delta; the badge is that
 *    delta. A deduplicated replay says so ("Already recorded"), and a ledger that disagrees with the
 *    split is reported, never hidden.
 * 4. **The sheet is frozen for the duration of the walk** — but not beyond it. Its query key is its
 *    own (`['count-shelf']`, exempt from the change feed on purpose) so a hub Refresh, a websocket
 *    change event or a window refocus cannot reorder rows under the operator's thumb — the
 *    press-station lesson, where an auto-refetch reordering rows turned a mis-tap into a money write.
 *    `gcTime: 0` then discards the snapshot on unmount. A refused save is retaken per ROW (a reload of
 *    that one blank gives the row its own `asOf`), so the other rows never move.
 * 5. **A refusal writes nothing and says why, in the server's words.** `moved_since_load` (something
 *    other than a seen sheet's press moved this bin), `pulled_rose` (a pull was recorded during the
 *    walk — answered right there, both answers persisted on the COUNT row), `pulled_changed` (a seen
 *    record moved or its sheet was deleted), `ledger_moving` (save again). Every one keeps the draft and
 *    the token.
 *
 * The one question the count still asks (§2.5 door 5): a sheet whose pull STARTED (its list or labels
 * printed, or the pull station opened) but which has no record for this blank. Its stack is shelf until
 * someone says otherwise, so Save waits for an answer per such sheet: *Its stack is at the press* ticks
 * that sheet's row for THIS blank at its listed quantity (via `count` — one row, never the whole sheet,
 * D2) and re-reads the row; *Its blanks are still on the shelf* dismisses it for the walk.
 *
 * On an old server (no count-sheet route) or a degraded read (the server could not derive the pulled
 * stacks) the page counts the LEGACY way — the figure typed is the book, with the old copy — so counting
 * is never blocked in either deploy order.
 *
 * There is no undo, because a COUNT re-anchors: re-counting a bin IS the correction, and both rows stay
 * in the ledger as honest history. Errors therefore never compound — one wrong entry is fully repaired
 * by one correct entry.
 */
import type { BatchPulledMap, BlankPulledSheet, BlankUnconfirmedSheet } from '@fsp/contracts';
import { BatchPulledMapSchema } from '@fsp/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearch } from '@tanstack/react-router';
import { AlertTriangle, ClipboardList, Loader2, RotateCcw } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { PAGE_CONTAINER_CLASS } from '@/components/page-container';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { useApiClient } from '@/lib/api/hooks';
import { useAuth } from '@/lib/auth-state';
import { cn } from '@/lib/utils';
import { pullRecordRows } from './batch-lib';
import {
  adoptSheetRevision,
  buildCountSheet,
  type ConfirmKind,
  type CountMode,
  type CountRow,
  type CountSource,
  confirmKind,
  doneBefore,
  expectedShelf,
  flattenSheet,
  isRecord,
  lastCountedLabel,
  legacySource,
  NO_ROSE_ANSWERS,
  nextUncounted,
  noSheetHolds,
  type PullAnswer,
  parseCount,
  parseRefusal,
  progressOf,
  pulledSummary,
  type Refusal,
  type RoseAnswers,
  rowFromDetail,
  sheetStrip,
  sheetTag,
  shelfBody,
  shortSheets,
  unansweredSheets,
  unconfirmedLine,
  withRoseAnswer,
} from './count-shelf-lib';
import { invalidateInventory } from './inventory-lib';

/** The walk's snapshot: the rows, how the page counts, and the server time the sheet loaded with. */
type SheetData = {
  mode: CountMode;
  sources: CountSource[];
  asOf?: string;
  note?: 'old-server' | 'degraded';
};

/** What a save left on the row — the LEDGER's figures (rule 3), never the submission. */
type SaveResult = {
  basis: 'book' | 'shelf' | 'take';
  /** On-hand as re-read after the write. */
  onHand: number;
  /** What the COUNT anchored the book to (`shelf + pulled` on the shelf basis). */
  countedQty: number;
  /** The server's delta when it reported one; else derived against the figure the sheet loaded with. */
  qtyDelta: number;
  shelfQty?: number;
  pulledQty?: number;
  deduplicated: boolean;
  /** Seen sheets whose `out` fell during the walk because the press drew them — written and reported. */
  fell: { tag: string; from: number; to: number }[];
};

type SaveOutcome = { kind: 'saved'; result: SaveResult } | { kind: 'refused'; refusal: Refusal };

/** Minutes a loaded sheet may sit before the strip nudges a reload (a refusal is the real guard). */
const STALE_SHEET_MIN = 20;
/** The strip names this many sheets before collapsing to "+N more". */
const STRIP_MAX_SHEETS = 5;
/** Between the re-reads after a "press" answer, while the index catches up with the header write. */
const PRESS_REREAD_DELAY_MS = 400;

export function CountShelfPage() {
  const client = useApiClient();
  const qc = useQueryClient();
  const { state } = useAuth();
  // Cosmetic mirror of the server gate (admin || inventory-admin); the 403 stays the real one.
  const canWrite = state.status === 'signed-in' && state.role.isInventoryWrite;
  // `/count?focus=<ssSku>` — the hub's "count it" items land on one bin. The cast is the codebase's
  // pattern for a loose read (`strict: false` types every route's params as optional unknowns); the
  // route's `validateSearch` is what guarantees `focus` is a non-empty string when present.
  const { focus } = useSearch({ strict: false }) as { focus?: string };

  // The walk's frozen snapshot — see rule 4. `gcTime: 0` scopes the freeze to this mount, so leaving
  // and coming back re-reads `lastCountedAt` and the resume markers stay honest.
  const sheet = useQuery({
    queryKey: ['count-shelf'],
    staleTime: Number.POSITIVE_INFINITY,
    gcTime: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    queryFn: async (): Promise<SheetData> => {
      const res = await client.GET('/inventory/count-sheet');
      if (res.response?.status === 404) {
        // An old server: no count-sheet route yet. Count the legacy way on the plain list.
        const { data, error } = await client.GET('/inventory');
        if (error) throw new Error(error?.error?.detail ?? 'Failed to load the shelf.');
        if (!data?.ok) throw new Error('Empty response from the server.');
        return { mode: 'legacy', sources: data.data.items.map(legacySource), note: 'old-server' };
      }
      if (res.error) throw new Error(res.error?.error?.detail ?? 'Failed to load the shelf.');
      if (!res.data?.ok) throw new Error('Empty response from the server.');
      const d = res.data.data;
      return {
        mode: d.degraded ? 'legacy' : 'shelf',
        sources: d.rows,
        asOf: d.asOf,
        note: d.degraded ? 'degraded' : undefined,
      };
    },
  });
  const mode: CountMode = sheet.data?.mode ?? 'shelf';
  const sheetAsOf = sheet.data?.asOf;

  // NOT frozen: a tab left open overnight must not keep calling yesterday's counts "today". The value
  // is only ever used for calendar-day comparisons and a relative label, so re-deriving costs nothing,
  // and freezing it at mount made the whole shelf read as counted-today after midnight.
  const now = new Date();
  // A minute ticker for the stale-sheet line only.
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 60_000);
    return () => clearInterval(id);
  }, []);

  const baseGroups = useMemo(() => buildCountSheet(sheet.data?.sources ?? []), [sheet.data]);
  /** ssSku → the row as this walk now knows it (a per-row reload, an adopted revision). React state,
   *  not the query cache: the snapshot stays the snapshot. */
  const [overrides, setOverrides] = useState<ReadonlyMap<string, CountRow>>(new Map());
  const groups = useMemo(
    () =>
      overrides.size === 0
        ? baseGroups
        : baseGroups.map((g) => ({ ...g, rows: g.rows.map((r) => overrides.get(r.ssSku) ?? r) })),
    [baseGroups, overrides],
  );

  /** ssSku → the on-hand the LEDGER reported after the write (rule 3), not the figure submitted. */
  const [saved, setSaved] = useState<ReadonlyMap<string, number>>(new Map());
  const [results, setResults] = useState<ReadonlyMap<string, SaveResult>>(new Map());
  const [draft, setDraft] = useState<ReadonlyMap<string, string>>(new Map());
  const [errors, setErrors] = useState<ReadonlyMap<string, string>>(new Map());
  const [refusals, setRefusals] = useState<ReadonlyMap<string, Refusal>>(new Map());
  const [confirming, setConfirming] = useState<
    ReadonlyMap<string, { kind: ConfirmKind; value: number }>
  >(new Map());
  const [pending, setPending] = useState<ReadonlySet<string>>(new Set());
  /** ssSku → batchId → the counter's answer to an unconfirmed sheet on that bin. */
  const [answers, setAnswers] = useState<ReadonlyMap<string, ReadonlyMap<string, PullAnswer>>>(
    new Map(),
  );
  /** `<ssSku>:<batchId>` while a "press" answer is being recorded. */
  const [answering, setAnswering] = useState<ReadonlySet<string>>(new Set());
  /** ssSku → the `pulled_rose` answers given so far for the figure being saved. */
  const [rose, setRose] = useState<ReadonlyMap<string, RoseAnswers>>(new Map());

  /**
   * Idempotency tokens, keyed `<ssSku>:<generation>:<figure>` — one token per OBSERVATION (rule 2).
   * A ref, not state: it must survive re-renders without causing them, and it must survive a Reload
   * (clearing it is what would turn an in-flight write's retry into a fresh, double-writing token).
   */
  const tokens = useRef(new Map<string, string>());
  /** ssSku → how many times this bin has been deliberately re-counted; bumps the token key. */
  const generations = useRef(new Map<string, number>());
  const inputs = useRef(new Map<string, HTMLInputElement>());
  const wrote = useRef(false);
  const focused = useRef(false);

  // The hub's queries aren't mounted here, so refreshing them per row would be wasted work. Refresh
  // once on the way out instead — and only if this walk actually moved the ledger.
  useEffect(() => {
    return () => {
      if (wrote.current) invalidateInventory(qc);
    };
  }, [qc]);

  // `?focus=` — once the sheet is up, land on that bin.
  useEffect(() => {
    if (!focus || !sheet.isSuccess || focused.current) return;
    const el = inputs.current.get(focus);
    if (!el) return;
    focused.current = true;
    el.scrollIntoView({ block: 'center' });
    el.focus();
  }, [focus, sheet.isSuccess]);

  const tokenFor = useCallback((ssSku: string, figure: number | 'take'): string => {
    const gen = generations.current.get(ssSku) ?? 0;
    const key = `${ssSku}:${gen}:${figure}`;
    let token = tokens.current.get(key);
    if (!token) {
      token = crypto.randomUUID();
      tokens.current.set(key, token);
    }
    return token;
  }, []);

  const write = useMutation({
    mutationFn: async ({
      row,
      shelfQty,
      answers: roseAnswers,
    }: {
      row: CountRow;
      shelfQty: number;
      answers: RoseAnswers;
    }): Promise<SaveOutcome> => {
      const token = tokenFor(row.ssSku, shelfQty);
      const asOf = row.pulled?.asOf ?? sheetAsOf;
      // The body: the typed figure plus advisory ids (rule 1). A legacy row (old server / degraded
      // read) posts the legacy book body — the figure typed IS the book, as before B2.
      const body =
        row.pulled && asOf
          ? shelfBody({
              shelfQty,
              asOf,
              sheets: row.pulled.sheets,
              answers: roseAnswers,
              clientToken: token,
            })
          : { countedQty: shelfQty, clientToken: token };
      const res = await client.POST('/inventory/{ssSku}/count', {
        params: { path: { ssSku: row.ssSku } },
        body,
      });
      if (res.error) {
        return { kind: 'refused', refusal: parseRefusal(res.error.error, res.response?.status) };
      }
      if (!res.data?.ok) throw new Error('Empty response from the server.');
      const d = res.data.data;
      // Rule 3: report the ledger's own figures. The server's delta is the badge; a response without
      // a movement block (an old server) falls back to the figure the sheet loaded with — display only.
      const m = d.movement;
      return {
        kind: 'saved',
        result: {
          basis: m?.basis ?? 'book',
          onHand: d.onHand,
          countedQty: m?.countedQty ?? d.onHand,
          qtyDelta: m?.qtyDelta ?? d.onHand - row.book,
          shelfQty: m?.shelfQty,
          pulledQty: m?.pulledQty,
          deduplicated: d.deduplicated === true,
          fell: (d.pulled?.fell ?? []).map((f) => ({ tag: sheetTag(f), from: f.from, to: f.to })),
        },
      };
    },
  });

  const save = useCallback(
    async (row: CountRow, shelfQty: number, roseAnswers: RoseAnswers) => {
      const ssSku = row.ssSku;
      // Whether the caret is still in this row decides whether we may move it once the write lands.
      const heldFocus = document.activeElement === inputs.current.get(ssSku);
      setConfirming((m) => without(m, ssSku));
      setErrors((m) => without(m, ssSku));
      setRefusals((m) => without(m, ssSku));
      setPending((s) => withKey(s, ssSku));
      try {
        const out = await write.mutateAsync({ row, shelfQty, answers: roseAnswers });
        if (out.kind === 'refused') {
          // Nothing was written. The draft and the token stay: the same figure resubmits as the same
          // observation once the counter has answered or looked again.
          setRefusals((m) => withEntry(m, ssSku, out.refusal));
          return;
        }
        wrote.current = true;
        setRose((m) => without(m, ssSku));
        setSaved((m) => withEntry(m, ssSku, out.result.onHand));
        setResults((m) => withEntry(m, ssSku, out.result));
        setDraft((m) => without(m, ssSku));
        // Only advance the caret if the operator has not already moved it themselves. A slow write
        // resolving after they have started keying the next bin would otherwise yank focus mid-number,
        // landing digits in a row they were not looking at — a wrong count on a bin nobody counted.
        const stillHere = document.activeElement === inputs.current.get(ssSku);
        if (heldFocus && (stillHere || document.activeElement === document.body)) {
          const next = nextUncounted(
            groups,
            withEntry(saved, ssSku, out.result.onHand),
            ssSku,
            now,
            mode,
          );
          if (next) inputs.current.get(next.ssSku)?.focus();
        }
      } catch (e) {
        // The token for THIS (bin, generation, figure) is deliberately kept, so "Try again" on the same
        // figure replays it and the server dedupes a lost response. A CHANGED figure keys a different
        // token, so it re-anchors instead of being silently discarded.
        setErrors((m) => withEntry(m, ssSku, e instanceof Error ? e.message : 'Count failed.'));
      } finally {
        setPending((s) => withoutKey(s, ssSku));
      }
    },
    [write, groups, saved, now, mode],
  );

  /**
   * Entry point from a row's Save / Enter / confirm. Always re-parses the CURRENT draft, so the figure
   * posted is by construction the figure on screen — an armed confirm cannot post a superseded value.
   */
  const attempt = useCallback(
    (row: CountRow, opts?: { confirmed?: boolean }) => {
      const n = parseCount(draft.get(row.ssSku) ?? '');
      if (n === null) return;
      if (unansweredSheets(row, answers.get(row.ssSku)).length > 0) return;
      if (!opts?.confirmed) {
        const kind = confirmKind(n, row);
        if (kind) {
          setConfirming((m) => withEntry(m, row.ssSku, { kind, value: n }));
          return;
        }
      }
      void save(row, n, rose.get(row.ssSku) ?? NO_ROSE_ANSWERS);
    },
    [draft, save, answers, rose],
  );

  /** A `pulled_rose` answer: resubmit the SAME figure (same token) with the answer added. */
  const answerRose = useCallback(
    (row: CountRow, sheetId: string, out: number, kind: 'counted' | 'press') => {
      const n = parseCount(draft.get(row.ssSku) ?? '');
      if (n === null) return;
      const next = withRoseAnswer(rose.get(row.ssSku) ?? NO_ROSE_ANSWERS, sheetId, out, kind);
      setRose((m) => withEntry(m, row.ssSku, next));
      void save(row, n, next);
    },
    [draft, rose, save],
  );

  /** Re-read ONE blank (`GET /inventory/:ssSku`) so a refused row can be retaken with a fresh `asOf`
   *  without reloading — and reordering — the whole sheet. */
  const reloadRow = useCallback(
    async (row: CountRow) => {
      const ssSku = row.ssSku;
      setPending((s) => withKey(s, ssSku));
      try {
        const { data, error } = await client.GET('/inventory/{ssSku}', {
          params: { path: { ssSku } },
        });
        if (error) throw new Error(error?.error?.detail ?? 'Could not re-read this blank.');
        if (!data?.ok) throw new Error('Empty response from the server.');
        const fresh = rowFromDetail(row, data.data);
        if (!fresh) {
          throw new Error(
            'The server could not read the pulled stacks for this blank — reload the sheet, or count it later.',
          );
        }
        setOverrides((m) => withEntry(m, ssSku, fresh));
        setRefusals((m) => without(m, ssSku));
        setErrors((m) => without(m, ssSku));
        setRose((m) => without(m, ssSku));
      } catch (e) {
        setErrors((m) =>
          withEntry(m, ssSku, e instanceof Error ? e.message : 'Could not re-read this blank.'),
        );
      } finally {
        setPending((s) => withoutKey(s, ssSku));
      }
    },
    [client],
  );

  /**
   * "Its stack is at the press": tick that sheet's row for THIS blank at its listed quantity, via
   * `count` (§2.5 door 5 — one row, never the whole sheet). The record is the COMPLETE map after the
   * write, so the sheet's current record is read first; a 409 `pull_changed` adopts the current record
   * and tries once more (or finds the row already ticked by someone else). Then this row is re-read
   * for its true block, with THIS sheet taken from the write's own response — the drawer read lists
   * sheets from an eventually consistent index that can lag the header by a moment — and every other
   * row holding the sheet adopts its new revision.
   */
  const answerPress = useCallback(
    async (row: CountRow, u: BlankUnconfirmedSheet) => {
      const ssSku = row.ssSku;
      const key = `${ssSku}:${u.batchId}`;
      setAnswering((s) => withKey(s, key));
      setErrors((m) => without(m, ssSku));
      try {
        const head = await client.GET('/batches/{batchId}', {
          params: { path: { batchId: u.batchId } },
        });
        if (head.response?.status === 404) {
          throw new Error(`Sheet ${sheetTag(u)} no longer exists — reload the sheet.`);
        }
        if (head.error) throw new Error(head.error?.error?.detail ?? 'Could not read the sheet.');
        if (!head.data?.ok) throw new Error('Empty response from the server.');
        let current: { pulled: BatchPulledMap; rev: number | null } = {
          pulled: head.data.data.batch.pulled ?? {},
          rev: head.data.data.batch.pulledRev ?? null,
        };
        let summary: { pulled?: BatchPulledMap; pulledRev?: number; status: string } | null = null;
        for (let attemptNo = 0; attemptNo < 2 && !summary; attemptNo += 1) {
          const existing = current.pulled[ssSku];
          if (existing) {
            // Already ticked (someone else, or our own write whose response was lost): nothing to send.
            summary = { pulled: current.pulled, pulledRev: current.rev ?? 0, status: 'READY' };
            break;
          }
          const res = await client.POST('/batches/{batchId}/pulled', {
            params: { path: { batchId: u.batchId } },
            body: {
              rev: current.rev,
              // The quantity is the sheet's own listed quantity for this blank — what the server read
              // from its items — never a cached count figure.
              rows: pullRecordRows(current.pulled, { ssSku, qty: u.qtyOnSheet }),
              via: 'count',
            },
          });
          if (res.error) {
            const err = res.error.error;
            if (err.type === 'pull_changed' && isRecord(err.current)) {
              const map = BatchPulledMapSchema.safeParse(err.current.pulled);
              current = {
                pulled: map.success ? map.data : {},
                rev: typeof err.current.pulledRev === 'number' ? err.current.pulledRev : null,
              };
              continue;
            }
            if (res.response?.status === 404) {
              throw new Error(`Sheet ${sheetTag(u)} no longer exists — reload the sheet.`);
            }
            throw new Error(err.detail ?? 'Could not record the pull.');
          }
          if (!res.data?.ok) throw new Error('Empty response from the server.');
          summary = res.data.data;
        }
        if (!summary) throw new Error('This sheet kept changing — try again.');
        const rev = summary.pulledRev ?? 0;
        const recorded = summary.pulled?.[ssSku];
        // Re-read this row for its true block (book, draws, the other sheets, a fresh asOf). The read
        // lists sheets off an eventually consistent index that can lag the header write by a moment,
        // so look again briefly until it carries this sheet at the revision the write landed at — then
        // its `drawn` is the ledger's and the row's `out` is exact. Bounded: after that the sheet is
        // taken from the write's own response (below) and a stale `out` is refused fail-safe.
        let fresh: CountRow | null = null;
        for (let look = 0; look < 3; look += 1) {
          if (look > 0) await new Promise((r) => setTimeout(r, PRESS_REREAD_DELAY_MS));
          const detail = await client.GET('/inventory/{ssSku}', { params: { path: { ssSku } } });
          if (detail.error) throw new Error(detail.error?.error?.detail ?? 'Could not re-read.');
          if (!detail.data?.ok) throw new Error('Empty response from the server.');
          fresh = rowFromDetail(row, detail.data.data);
          if (!fresh?.pulled) break;
          if (fresh.pulled.sheets.some((s) => s.batchId === u.batchId && s.rev >= rev)) break;
        }
        if (!fresh?.pulled) {
          throw new Error(
            'Recorded on the sheet, but the pulled stacks could not be re-read — reload the sheet before saving this bin.',
          );
        }
        // …with THIS sheet taken from the write itself (the index can lag): off the question, on the
        // recorded list at the revision the write landed at.
        const seen = fresh.pulled.sheets.find((s) => s.batchId === u.batchId);
        const own: BlankPulledSheet = {
          batchId: u.batchId,
          seq: u.seq,
          status: summary.status,
          recorded: recorded?.qty ?? u.qtyOnSheet,
          drawn: seen?.drawn ?? 0,
          out: Math.max(0, (recorded?.qty ?? u.qtyOnSheet) - (seen?.drawn ?? 0)),
          onSheet: u.qtyOnSheet,
          rev,
          at: recorded?.at ?? new Date().toISOString(),
          via: recorded?.via ?? 'count',
          who: recorded?.who,
        };
        const freshBlock = fresh.pulled;
        setOverrides((m) => {
          const next = new Map(m);
          // Earlier "press" answers on THIS row (other sheets ticked a moment ago via `count`) that the
          // lagging read does not list yet are carried over from the row as it stands — a re-read that
          // dropped them would send a `pulledSeen` missing a stack this walk itself recorded (refused as
          // `pulled_rose`) and under-display `now` (review finding).
          const base = next.get(ssSku) ?? row;
          const ownEarlier = (base.pulled?.sheets ?? []).filter(
            (s) =>
              s.via === 'count' &&
              s.batchId !== u.batchId &&
              !freshBlock.sheets.some((x) => x.batchId === s.batchId),
          );
          const sheets = [
            ...freshBlock.sheets.filter((s) => s.batchId !== u.batchId),
            own,
            ...ownEarlier,
          ].sort((a, b) => (a.seq ?? 0) - (b.seq ?? 0) || a.batchId.localeCompare(b.batchId));
          const answered = new Set(sheets.map((s) => s.batchId));
          const merged: CountRow = {
            ...fresh,
            pulled: {
              ...freshBlock,
              sheets,
              unconfirmed: freshBlock.unconfirmed.filter((x) => !answered.has(x.batchId)),
              now: sheets.reduce((n, s) => n + s.out, 0),
            },
          };
          next.set(ssSku, merged);
          // Every other row holding this sheet adopts its new revision (its quantities are its own).
          for (const r of flattenSheet(groups)) {
            if (r.ssSku === ssSku) continue;
            const adopted = adoptSheetRevision(next.get(r.ssSku) ?? r, u.batchId, rev);
            if (adopted !== (next.get(r.ssSku) ?? r)) next.set(r.ssSku, adopted);
          }
          return next;
        });
        setAnswers((m) =>
          withEntry(m, ssSku, withEntry(m.get(ssSku) ?? new Map(), u.batchId, 'press')),
        );
      } catch (e) {
        setErrors((m) =>
          withEntry(m, ssSku, e instanceof Error ? e.message : 'Could not record the pull.'),
        );
      } finally {
        setAnswering((s) => withoutKey(s, key));
      }
    },
    [client, groups],
  );

  /** "Everything of this size at the press is pulled": each sheet in turn — never in parallel, so each
   *  re-read sees the previous tick and no two merges race for the row (review finding). */
  const answerAllPress = useCallback(
    async (row: CountRow, sheets: readonly BlankUnconfirmedSheet[]) => {
      for (const u of sheets) await answerPress(row, u);
    },
    [answerPress],
  );

  /**
   * The off-system-job door (D12 / v1 Q13): a stocked bin counted to ZERO with no sheet holding the
   * blank — the stock went to a job the system never saw. A COUNT to 0 would be "right" for the shelf
   * and silent for the reorder model; a Take (a manual CONSUME) records the consumption. The quantity
   * is the book as RE-READ at the tap, never the figure the sheet loaded with (rule 1 — a press could
   * have drawn some since). One token per (bin, generation): a retry replays, a re-count re-keys.
   */
  const takeForJob = useCallback(
    async (row: CountRow) => {
      const ssSku = row.ssSku;
      setPending((s) => withKey(s, ssSku));
      setErrors((m) => without(m, ssSku));
      try {
        const fresh = await client.GET('/inventory/{ssSku}', { params: { path: { ssSku } } });
        if (fresh.error) throw new Error(fresh.error?.error?.detail ?? 'Could not re-read.');
        if (!fresh.data?.ok) throw new Error('Empty response from the server.');
        const onHand = fresh.data.data.onHand.onHand;
        if (onHand <= 0) {
          throw new Error('The book already reads 0 for this blank — save the count instead.');
        }
        const res = await client.POST('/inventory/{ssSku}/consume', {
          params: { path: { ssSku } },
          body: {
            quantity: onHand,
            reason: 'taken for a job not in the system — counted 0 on the shelf',
            clientToken: tokenFor(ssSku, 'take'),
          },
        });
        if (res.error) throw new Error(res.error?.error?.detail ?? 'Take failed.');
        if (!res.data?.ok) throw new Error('Empty response from the server.');
        wrote.current = true;
        const after = res.data.data.onHand;
        setConfirming((m) => without(m, ssSku));
        setDraft((m) => without(m, ssSku));
        setSaved((m) => withEntry(m, ssSku, after));
        setResults((m) =>
          withEntry(m, ssSku, {
            basis: 'take',
            onHand: after,
            countedQty: 0,
            qtyDelta: -onHand,
            deduplicated: false,
            fell: [],
          }),
        );
      } catch (e) {
        setErrors((m) => withEntry(m, ssSku, e instanceof Error ? e.message : 'Take failed.'));
      } finally {
        setPending((s) => withoutKey(s, ssSku));
      }
    },
    [client, tokenFor],
  );

  const progress = progressOf(groups, saved, now, mode);
  const pct = progress.total === 0 ? 0 : Math.round((100 * progress.done) / progress.total);
  const strip = useMemo(() => sheetStrip(flattenSheet(groups)), [groups]);
  const loadedMin = sheet.dataUpdatedAt
    ? Math.floor((Date.now() - sheet.dataUpdatedAt) / 60_000)
    : 0;

  return (
    <div className={cn(PAGE_CONTAINER_CLASS, 'space-y-4 pb-16')}>
      {/* ⚠️ `top-14`, not `top-0`. The app shell's header is `sticky top-0 z-30` and 56px tall, so a
          `top-0` here would pin this behind it (z-20 < z-30) and the count progress + Reload control
          would vanish under the shell exactly when the operator scrolls the walk. */}
      <header className="sticky top-14 z-20 -mx-1 space-y-2 border-b bg-background px-1 pb-3 pt-1">
        <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
          <div>
            <h1 className="text-lg font-semibold tracking-tight">Count the shelf</h1>
            <p className="text-xs text-muted-foreground">
              {progress.done} of {progress.total} counted
              {progress.carriedIn > 0 && ` · ${progress.carriedIn} already done earlier today`}
              {progress.mismatched > 0 && ` · ${progress.mismatched} didn't match`}
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            // Disabled while anything is in flight: reloading under a live write would discard the row
            // states that tell the operator whether it landed.
            disabled={sheet.isFetching || pending.size > 0 || answering.size > 0}
            onClick={() => {
              if (wrote.current) invalidateInventory(qc);
              void qc.resetQueries({ queryKey: ['count-shelf'], exact: true });
              setOverrides(new Map());
              setSaved(new Map());
              setResults(new Map());
              setDraft(new Map());
              setErrors(new Map());
              setRefusals(new Map());
              setConfirming(new Map());
              setAnswers(new Map());
              setRose(new Map());
              // `tokens` and `generations` are deliberately NOT cleared — they exist to survive exactly
              // this, and a fresh token for an already-written figure is a double write.
            }}
          >
            <RotateCcw className={cn('size-4', sheet.isFetching && 'animate-spin')} />
            Reload sheet
          </Button>
        </div>
        <div className="h-1.5 overflow-hidden rounded-full bg-muted">
          <div
            className="h-full rounded-full bg-primary transition-[width] duration-300"
            style={{ width: `${pct}%` }}
          />
        </div>
        {mode === 'shelf' ? (
          <p className="rounded-md bg-muted px-2.5 py-1.5 text-xs leading-snug text-muted-foreground">
            Count what is <span className="font-medium text-foreground">on the shelf</span>. Blanks
            recorded as pulled at the pull station are added for you. A blank pulled for a sheet
            nobody confirmed is shelf until someone confirms it — the row will say. Caps: count
            every cap blank, staged or not.
          </p>
        ) : (
          <p className="rounded-md bg-muted px-2.5 py-1.5 text-xs leading-snug text-muted-foreground">
            Count every <span className="font-medium text-foreground">unprinted</span> garment — on
            the shelf <span className="font-medium text-foreground">and</span> any stack already
            pulled to the press.
            {sheet.data?.note === 'old-server' && (
              <>
                {' '}
                <span className="font-medium text-foreground">
                  Server not yet updated for shelf counts — counting the old way: count pulled
                  stacks in.
                </span>
              </>
            )}
            {sheet.data?.note === 'degraded' && (
              <>
                {' '}
                <span className="font-medium text-foreground">
                  The pulled stacks couldn't be read — counting the old way: count pulled stacks in.
                </span>
              </>
            )}
          </p>
        )}
        {mode === 'shelf' && sheet.isSuccess && (
          <SheetStripLine strip={strip} loadedMin={loadedMin} />
        )}
      </header>

      {!canWrite && (
        <Alert>
          <AlertTitle>Read-only</AlertTitle>
          <AlertDescription>
            Saving a count needs the admin or inventory-admin group. You can walk the sheet, but the
            numbers won't record.
          </AlertDescription>
        </Alert>
      )}

      {sheet.isError && (
        <Alert variant="destructive">
          <AlertTitle>Couldn't load the shelf</AlertTitle>
          <AlertDescription>
            {sheet.error instanceof Error ? sheet.error.message : 'Network error.'}
          </AlertDescription>
        </Alert>
      )}

      {sheet.isPending && <Skeleton className="h-64 w-full" />}

      {sheet.isSuccess && groups.length === 0 && (
        <div className="flex flex-col items-center gap-2 rounded-lg border border-dashed py-16 text-center">
          <ClipboardList className="size-8 text-muted-foreground" />
          <p className="text-sm font-medium">No blanks to count</p>
          <p className="max-w-sm text-sm text-muted-foreground">
            Nothing has a ledger history yet — receive a shipment or use Restock on the inventory
            hub, and the shelf will show up here.
          </p>
        </div>
      )}

      {groups.map((g) => (
        <section key={g.key} className="overflow-hidden rounded-lg border">
          <h2 className="border-b bg-muted/50 px-3 py-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            {g.label}
            <span className="ml-2 font-normal normal-case tracking-normal">
              {g.rows.filter((r) => saved.has(r.ssSku) || doneBefore(r, now, mode)).length}/
              {g.rows.length}
            </span>
          </h2>
          <ul>
            {g.rows.map((row) => (
              <BinRow
                key={row.ssSku}
                row={row}
                mode={mode}
                now={now}
                canWrite={canWrite}
                result={results.get(row.ssSku)}
                draftValue={draft.get(row.ssSku) ?? ''}
                error={errors.get(row.ssSku)}
                refusal={refusals.get(row.ssSku)}
                confirm={confirming.get(row.ssSku)}
                busy={pending.has(row.ssSku)}
                answers={answers.get(row.ssSku)}
                answering={answering}
                registerInput={(el) => {
                  if (el) inputs.current.set(row.ssSku, el);
                  else inputs.current.delete(row.ssSku);
                }}
                onDraft={(v) => {
                  setDraft((m) => withEntry(m, row.ssSku, v));
                  // Editing the field retracts any armed confirm and any refusal's question, so one
                  // row can never carry two primary buttons offering two different numbers — and drops
                  // the `pulled_rose` answers, which were about THAT figure's observation: an exclude
                  // riding along with a new figure would take a stack out of the book with no refusal.
                  setConfirming((m) => without(m, row.ssSku));
                  setRefusals((m) => without(m, row.ssSku));
                  setRose((m) => without(m, row.ssSku));
                }}
                onAttempt={() => attempt(row)}
                onConfirm={() => attempt(row, { confirmed: true })}
                onCancelConfirm={() => {
                  setConfirming((m) => without(m, row.ssSku));
                  setDraft((m) => without(m, row.ssSku));
                  inputs.current.get(row.ssSku)?.focus();
                }}
                onReopen={() => {
                  // A deliberate re-count is a NEW observation: bump the generation so it gets a fresh
                  // token even if the operator keys the same figure, and drop it from `saved` so the
                  // progress line stops claiming a bin that is being re-counted.
                  generations.current.set(row.ssSku, (generations.current.get(row.ssSku) ?? 0) + 1);
                  setSaved((m) => without(m, row.ssSku));
                  setResults((m) => without(m, row.ssSku));
                  setDraft((m) => without(m, row.ssSku));
                  setRose((m) => without(m, row.ssSku));
                  requestAnimationFrame(() => inputs.current.get(row.ssSku)?.focus());
                }}
                onAnswer={(u, kind) => {
                  if (kind === 'shelf') {
                    setAnswers((m) =>
                      withEntry(
                        m,
                        row.ssSku,
                        withEntry(m.get(row.ssSku) ?? new Map(), u.batchId, 'shelf'),
                      ),
                    );
                    return;
                  }
                  void answerPress(row, u);
                }}
                onAnswerAll={(list) => void answerAllPress(row, list)}
                onRose={(sheetId, out, kind) => answerRose(row, sheetId, out, kind)}
                onReloadRow={() => void reloadRow(row)}
                onTake={() => void takeForJob(row)}
              />
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}

/** "Out at the press · 100 blanks on 4 confirmed sheets · #215 printed, not confirmed" + the stale line. */
function SheetStripLine({
  strip,
  loadedMin,
}: {
  strip: ReturnType<typeof sheetStrip>;
  loadedMin: number;
}) {
  const tags = (xs: { tag: string }[]) =>
    xs.length <= STRIP_MAX_SHEETS
      ? xs.map((s) => s.tag).join(' · ')
      : `${xs
          .slice(0, STRIP_MAX_SHEETS)
          .map((s) => s.tag)
          .join(' · ')} +${xs.length - STRIP_MAX_SHEETS} more`;
  const parts: string[] = [];
  if (strip.confirmed.length > 0) {
    parts.push(
      `${strip.blanksOut} blanks on ${strip.confirmed.length} confirmed sheet${strip.confirmed.length === 1 ? '' : 's'} (${tags(strip.confirmed)})`,
    );
  }
  if (strip.unconfirmed.length > 0) {
    parts.push(`${tags(strip.unconfirmed)} started, not confirmed`);
  }
  if (strip.stillOut.length > 0) {
    parts.push(
      strip.stillOut
        .slice(0, STRIP_MAX_SHEETS)
        .map((s) => `${s.out} still out on closed ${s.tag}`)
        .join(' · '),
    );
  }
  return (
    <div className="space-y-0.5 text-xs text-muted-foreground" data-testid="sheet-strip">
      <p>
        <span className="font-medium text-foreground">Out at the press</span>
        {parts.length === 0 ? ' · nothing recorded as pulled' : ` · ${parts.join(' · ')}`}
      </p>
      {loadedMin >= STALE_SHEET_MIN && (
        <p className="text-amber-700 dark:text-amber-400">
          Sheet loaded {loadedMin} min ago — a receipt, a Take or a press on an unconfirmed sheet
          since then will be refused; reload the row it names, or the sheet.
        </p>
      )}
    </div>
  );
}

function BinRow({
  row,
  mode,
  now,
  canWrite,
  result,
  draftValue,
  error,
  refusal,
  confirm,
  busy,
  answers,
  answering,
  registerInput,
  onDraft,
  onAttempt,
  onConfirm,
  onCancelConfirm,
  onReopen,
  onAnswer,
  onAnswerAll,
  onRose,
  onReloadRow,
  onTake,
}: {
  row: CountRow;
  mode: CountMode;
  now: Date;
  canWrite: boolean;
  result: SaveResult | undefined;
  draftValue: string;
  error: string | undefined;
  refusal: Refusal | undefined;
  confirm: { kind: ConfirmKind; value: number } | undefined;
  busy: boolean;
  answers: ReadonlyMap<string, PullAnswer> | undefined;
  answering: ReadonlySet<string>;
  registerInput: (el: HTMLInputElement | null) => void;
  onDraft: (v: string) => void;
  onAttempt: () => void;
  onConfirm: () => void;
  onCancelConfirm: () => void;
  onReopen: () => void;
  onAnswer: (u: BlankUnconfirmedSheet, kind: PullAnswer) => void;
  onAnswerAll: (sheets: BlankUnconfirmedSheet[]) => void;
  onRose: (sheetId: string, out: number, kind: 'counted' | 'press') => void;
  onReloadRow: () => void;
  onTake: () => void;
}) {
  const done = result !== undefined;
  const delta = result?.qtyDelta ?? 0;
  const carried = !done && doneBefore(row, now, mode);
  const valid = parseCount(draftValue) !== null;
  const pulled = row.pulled;
  const unanswered = unansweredSheets(row, answers);
  const shorts = pulled ? shortSheets(pulled.sheets) : [];
  const fieldLabel = pulled ? 'On the shelf' : 'Counted';
  const n = parseCount(draftValue);
  const noSheet = noSheetHolds(row);

  return (
    <li
      data-testid="bin-row"
      data-sku={row.ssSku}
      className={cn(
        'flex flex-wrap items-center gap-x-3 gap-y-2 border-b px-3 py-2.5 last:border-b-0',
        done && delta === 0 && 'bg-emerald-50/70 dark:bg-emerald-950/20',
        done && delta !== 0 && 'bg-amber-50/70 dark:bg-amber-950/20',
        carried && 'bg-muted/40',
      )}
    >
      {row.badge && (
        <span
          className={cn(
            'grid h-11 w-12 shrink-0 place-items-center rounded-md border text-base font-bold',
            done ? 'border-transparent bg-background' : 'bg-muted/60',
          )}
        >
          {row.badge}
        </span>
      )}

      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-medium">{row.title}</div>
        <div className="text-xs text-muted-foreground" data-testid="bin-subline">
          {row.variant} · was {row.book}
          {pulled && pulled.now > 0 && (
            <>
              {' '}
              on the books · {pulledSummary(pulled.sheets)} · expect ≈
              {expectedShelf(row.book, pulled.now)} on the shelf
            </>
          )}
          {' · '}
          {lastCountedLabel(row.lastCountedAt, now)}
        </div>
        {shorts.length > 0 && (
          <div className="text-xs text-amber-700 dark:text-amber-400">
            {shorts
              .map(
                ({ sheet, short }) =>
                  `short at the pull: got ${sheet.recorded} of ${sheet.onSheet} on ${sheetTag(sheet)} (${short} short)`,
              )
              .join(' · ')}
          </div>
        )}
        {pulled && pulled.stillOut.length > 0 && (
          <div className="text-xs text-amber-700 dark:text-amber-400">
            {pulled.stillOut
              .map((s) => `+${s.out} still out on closed ${sheetTag(s)} — returned?`)
              .join(' · ')}
          </div>
        )}
      </div>

      {done ? (
        <div className="flex shrink-0 items-center gap-2">
          <span className="text-xl font-bold tabular-nums">
            {result.basis === 'take' ? result.onHand : result.countedQty}
          </span>
          <span
            className={cn(
              'rounded-full border px-2 py-0.5 text-[11px] font-semibold',
              delta === 0
                ? 'border-emerald-300 text-emerald-700 dark:border-emerald-800 dark:text-emerald-400'
                : 'border-amber-300 text-amber-700 dark:border-amber-800 dark:text-amber-400',
            )}
          >
            {result.basis === 'take'
              ? 'taken'
              : delta === 0
                ? 'matches'
                : delta > 0
                  ? `+${delta}`
                  : delta}
          </span>
          <Button variant="ghost" size="sm" onClick={onReopen} disabled={!canWrite}>
            Re-count
          </Button>
        </div>
      ) : (
        <div className="flex shrink-0 items-center gap-2">
          {carried && (
            <span className="rounded-full border px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
              done today
            </span>
          )}
          <Input
            ref={registerInput}
            type="text"
            inputMode="numeric"
            enterKeyHint="done"
            autoComplete="off"
            placeholder="—"
            aria-label={`${fieldLabel} for ${row.title} ${row.variant}`}
            className="h-11 w-20 text-center text-lg font-semibold tabular-nums"
            value={draftValue}
            // Deliberately NOT disabled while a write is in flight: a disabled input drops focus, and
            // the caret then lands wherever the browser decides — type-ahead would go to another bin.
            // The Save button's `busy` guard plus the per-observation token are what stop a re-submit.
            disabled={!canWrite}
            onFocus={(e) => e.currentTarget.select()}
            onChange={(e) => onDraft(e.currentTarget.value.replace(/\D/g, '').slice(0, 4))}
            onKeyDown={(e) => {
              if (e.key !== 'Enter') return;
              e.preventDefault();
              onAttempt();
            }}
          />
          <Button
            size="sm"
            className="h-11"
            onClick={onAttempt}
            disabled={!canWrite || !valid || busy || unanswered.length > 0}
          >
            {busy && <Loader2 className="size-4 animate-spin" />}
            Save
          </Button>
        </div>
      )}

      {/* The one question (door 5): a started sheet with no record for this blank. Save waits. */}
      {!done && pulled && unanswered.length > 0 && (
        <div
          className="w-full space-y-1.5 rounded-md border border-amber-300 bg-amber-50 px-2.5 py-2 text-xs dark:border-amber-800 dark:bg-amber-950/40"
          data-testid="unconfirmed-question"
        >
          {unanswered.map((u) => {
            const pendingThis = answering.has(`${row.ssSku}:${u.batchId}`);
            return (
              // Phone: the evidence on its own line, the two answers in a row under it (a narrow text
              // column beside one button and the other button orphaned below read badly at 390 px).
              // Desktop: one line, the answers trailing.
              <div
                key={u.batchId}
                className="space-y-2 sm:flex sm:flex-wrap sm:items-center sm:gap-2 sm:space-y-0"
              >
                <span className="block text-amber-900 sm:flex-1 dark:text-amber-200">
                  {unconfirmedLine(u)}
                </span>
                {/* 44 px targets: this is answered with a thumb at the shelf, like Save. */}
                <div className="flex flex-wrap gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-11"
                    disabled={!canWrite || pendingThis}
                    onClick={() => onAnswer(u, 'press')}
                  >
                    {pendingThis && <Loader2 className="size-4 animate-spin" />}
                    Its stack is at the press
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-11"
                    disabled={!canWrite || pendingThis}
                    onClick={() => onAnswer(u, 'shelf')}
                  >
                    Its blanks are still on the shelf
                  </Button>
                </div>
              </div>
            );
          })}
          {unanswered.length > 1 && (
            <div className="flex justify-end">
              <Button
                size="sm"
                variant="outline"
                disabled={!canWrite || answering.size > 0}
                onClick={() => onAnswerAll(unanswered)}
              >
                Everything of this size at the press is pulled
              </Button>
            </div>
          )}
        </div>
      )}
      {!done && answers && [...answers.entries()].some(([, v]) => v === 'shelf') && (
        <p className="w-full text-xs text-muted-foreground">
          {[...answers.entries()]
            .filter(([, v]) => v === 'shelf')
            .map(([id]) => {
              const u = pulled?.unconfirmed.find((x) => x.batchId === id);
              return `${u ? sheetTag(u) : id.slice(0, 8)}: counted as shelf for this walk`;
            })
            .join(' · ')}
        </p>
      )}

      {/* After a save: the LEDGER's split and the SERVER's delta (rule 3). */}
      {done && result.basis === 'shelf' && (
        <p className="w-full text-xs text-muted-foreground" data-testid="saved-line">
          {result.deduplicated ? 'Already recorded: ' : 'On the shelf '}
          <span className="font-medium text-foreground">{result.shelfQty ?? '?'}</span>
          {result.deduplicated ? ' on the shelf' : ''}
          {(result.pulledQty ?? 0) > 0 && (
            <>
              {' '}
              + pulled <span className="font-medium text-foreground">{result.pulledQty}</span>
            </>
          )}{' '}
          {result.deduplicated ? '→' : '='} {result.countedQty} on the books ·{' '}
          {delta === 0 ? '0' : delta > 0 ? `+${delta}` : delta} from the book
          {result.fell.length > 0 &&
            ` · ${result.fell.map((f) => `${f.tag} pressed its ${f.from - f.to} while you counted`).join(' · ')}`}
        </p>
      )}
      {done && result.basis === 'book' && result.deduplicated && (
        <p className="w-full text-xs text-muted-foreground" data-testid="saved-line">
          Already recorded: {result.countedQty} on the books.
        </p>
      )}
      {done && result.basis === 'take' && (
        <p className="w-full text-xs text-muted-foreground" data-testid="saved-line">
          Recorded as taken for a job: {-delta} off the book → {result.onHand}. This raises the
          reorder level for this blank.
        </p>
      )}

      {/* The ledger disagreed with what the count anchored — a movement that raced the re-read.
          Reported rather than hidden, because a clean "saved" here would be a lie. */}
      {done && result.basis !== 'take' && result.onHand !== result.countedQty && (
        <div className="flex w-full items-center gap-2 rounded-md border border-amber-300 bg-amber-50 px-2.5 py-2 text-xs text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200">
          <AlertTriangle className="size-4 shrink-0" />
          <span className="flex-1">
            The count anchored <span className="font-semibold">{result.countedQty}</span>, but the
            ledger now reads <span className="font-semibold">{result.onHand}</span>. Something moved
            this bin while you were counting it — count it again to be sure.
          </span>
        </div>
      )}

      {confirm !== undefined && (
        <div
          role="alert"
          className="flex w-full flex-wrap items-center gap-2 rounded-md border border-amber-300 bg-amber-50 px-2.5 py-2 text-xs dark:border-amber-800 dark:bg-amber-950/40"
        >
          <AlertTriangle className="size-4 shrink-0 text-amber-700 dark:text-amber-400" />
          <span className="flex-1 text-amber-900 dark:text-amber-200">
            {confirm.kind === 'whole-book' && pulled ? (
              <>
                Nothing on the shelf — the <span className="font-semibold">{pulled.now}</span> on
                the books would all be pull records (
                {pulledSummary(pulled.sheets).replace(/^\d+ pulled \(|\)$/g, '')}). Is every one of
                them really at the press?
              </>
            ) : (
              <>
                <span className="font-semibold">{confirm.value}</span>
                {pulled && pulled.now > 0
                  ? ` on the shelf + ${pulled.now} pulled = ${confirm.value + pulled.now}, which is `
                  : ' is '}
                {(() => {
                  const book = confirm.value + (pulled?.now ?? 0);
                  const d = book - row.book;
                  return d > 0 ? `+${d}` : d;
                })()} from the {row.book} this sheet loaded with. Count it again, or confirm.
              </>
            )}
            {confirm.kind === 'zero' && noSheet && (
              <>
                {' '}
                No sheet holds this blank. Were these pulled for a job that is not in the system?
              </>
            )}
          </span>
          <Button variant="outline" size="sm" onClick={onCancelConfirm}>
            Count again
          </Button>
          {confirm.kind === 'zero' && noSheet && (
            <Button variant="outline" size="sm" onClick={onTake} disabled={busy}>
              {busy && <Loader2 className="size-4 animate-spin" />}
              Record as taken for a job
            </Button>
          )}
          <Button size="sm" onClick={onConfirm} disabled={busy}>
            {busy && <Loader2 className="size-4 animate-spin" />}
            {confirm.kind === 'zero' && noSheet
              ? "It's a miscount — 0"
              : `Yes — it's ${confirm.value}`}
          </Button>
        </div>
      )}

      {/* A refusal: nothing was written; the server's words, and the way back. */}
      {refusal && (
        <div
          role="alert"
          data-testid="refusal"
          data-kind={refusal.kind}
          className="flex w-full flex-wrap items-center gap-2 rounded-md border border-destructive/40 bg-destructive/5 px-2.5 py-2 text-xs"
        >
          <span className="w-full font-medium">{refusal.title}</span>
          <span className="flex-1">{refusal.detail} Nothing was written.</span>
          {refusal.kind === 'rose' && refusal.sheets[0] && n !== null && (
            <>
              <span className="w-full">
                Were its {refusal.sheets[0].out} {row.variant.split(' · ').at(-1) ?? ''} on the
                shelf when you counted {n}?
              </span>
              <Button
                size="sm"
                variant="outline"
                disabled={busy}
                onClick={() =>
                  onRose(refusal.sheets[0]?.batchId ?? '', refusal.sheets[0]?.out ?? 0, 'counted')
                }
              >
                Yes, I counted them
              </Button>
              <Button
                size="sm"
                disabled={busy}
                onClick={() =>
                  onRose(refusal.sheets[0]?.batchId ?? '', refusal.sheets[0]?.out ?? 0, 'press')
                }
              >
                {busy && <Loader2 className="size-4 animate-spin" />}
                No, they were at the press
              </Button>
            </>
          )}
          {(refusal.kind === 'changed' || refusal.kind === 'moved' || refusal.kind === 'stale') && (
            <Button size="sm" variant="outline" disabled={busy} onClick={onReloadRow}>
              {busy && <Loader2 className="size-4 animate-spin" />}
              Reload this row
            </Button>
          )}
          {(refusal.kind === 'moving' || refusal.kind === 'other') && (
            <Button size="sm" variant="outline" disabled={busy} onClick={onAttempt}>
              Save again
            </Button>
          )}
        </div>
      )}

      {error && (
        <div className="flex w-full items-center gap-2 text-xs text-destructive">
          <span className="flex-1">{error}</span>
          {!done && (
            <Button variant="outline" size="sm" onClick={onAttempt} disabled={busy || !valid}>
              Try again
            </Button>
          )}
        </div>
      )}
    </li>
  );
}

/* Immutable Map/Set helpers — new instances so React sees the change. */
function withEntry<V>(m: ReadonlyMap<string, V>, k: string, v: V): ReadonlyMap<string, V> {
  const next = new Map(m);
  next.set(k, v);
  return next;
}
function without<V>(m: ReadonlyMap<string, V>, k: string): ReadonlyMap<string, V> {
  const next = new Map(m);
  next.delete(k);
  return next;
}
function withKey(s: ReadonlySet<string>, k: string): ReadonlySet<string> {
  const next = new Set(s);
  next.add(k);
  return next;
}
function withoutKey(s: ReadonlySet<string>, k: string): ReadonlySet<string> {
  const next = new Set(s);
  next.delete(k);
  return next;
}
